// react
import { useEffect, useState } from 'react';

// types
import { Account } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { alpha, Box, Container, Grid, Stack, Tooltip, Typography } from '@mui/material';

// pages
import { Entities } from './Entities/Entities';
import { Financing } from './Financing/Financing';

// ui
import { VisibilityTextToggle } from '../../../ui/VisibilityTextToggle/VisibilityTextToggle';

// cards
import { CardKeyValue } from '../../../cards/CardKeyValue/CardKeyValue';
import { CardReimbursed } from '../../../cards/CardReimbursed/CardReimbursed';

// utils
import { formatCurrency } from '../../../../utils/currency';

// spend plan services
import { AccountEarmarkSummary, getAccountEarmarks } from '../../../../services/api';

// props
export interface DetailsProps {
	account: Account;
}

export const Details = ({ account }: DetailsProps) => {
	// hooks
	const { t } = useTranslation();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [earmarks, setEarmarks] = useState<AccountEarmarkSummary | null>(null);

	const balance = account.balances.reduce((total, balance) => {
		return total + balance.balance;
	}, 0);

	// Fetch earmark data
	useEffect(() => {
		if (!account.id || !accessToken || !idToken) return;
		let cancelled = false;
		getAccountEarmarks({ accessToken, idToken }, { account: account.id }).then((res) => {
			if (cancelled) return;
			if (!res.error && res.data && res.data.length > 0) {
				setEarmarks(res.data[0]);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [account.id, accessToken, idToken]);

	const hasEarmarks =
		earmarks && (earmarks.pendingEarmarked > 0 || earmarks.finalizedEarmarked > 0);

	const md = account.isReimbursement ? 3 : 4;

	return (
		<Container maxWidth={false}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={md}>
					<CardKeyValue
						label={{ children: t('institution') }}
						value={{
							children: account.institution || t('na'),
							color: 'common.black',
							fontWeight: 500,
							variant: 'body1',
						}}
					/>
				</Grid>
				<Grid item xs={12} md={md}>
					<CardKeyValue
						label={{ children: t('number') }}
						value={{
							children: account.num ? <VisibilityTextToggle text={account.num} /> : t('na'),
							color: 'common.black',
							fontWeight: 500,
							variant: 'body1',
						}}
					/>
				</Grid>
				<Grid item xs={12} md={md}>
					<CardKeyValue
						label={{ children: t('balance') }}
						value={{
							children: formatCurrency({
								currency: account.currency,
								value: balance,
							}),
						}}
					/>
				</Grid>

				{/* Earmark Summary Row */}
				{hasEarmarks && earmarks && (
					<Grid item xs={12}>
						<Box
							sx={{
								border: '1px solid',
								borderColor: alpha('#1976d2', 0.15),
								borderRadius: 2,
								bgcolor: alpha('#1976d2', 0.03),
								px: 2.5,
								py: 2,
							}}>
							<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#1976d2' }}>
								Spend Plan Earmarks
							</Typography>
							<Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', gap: 2 }}>
								<Tooltip title="Earmarks from DRAFT or SUBMITTED spend plans">
									<Box>
										<Typography variant="caption" color="text.secondary">
											Pending Earmarks
										</Typography>
										<Typography variant="body1" sx={{ fontWeight: 600, color: '#ed6c02' }}>
											{formatCurrency({
												value: earmarks.pendingEarmarked,
												currency: account.currency,
											})}
										</Typography>
									</Box>
								</Tooltip>
								<Tooltip title="Earmarks from APPROVED or COMPLETED spend plans">
									<Box>
										<Typography variant="caption" color="text.secondary">
											Finalized Earmarks
										</Typography>
										<Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
											{formatCurrency({
												value: earmarks.finalizedEarmarked,
												currency: account.currency,
											})}
										</Typography>
									</Box>
								</Tooltip>
								<Box>
									<Typography variant="caption" color="text.secondary">
										Available
									</Typography>
									<Typography
										variant="body1"
										sx={{
											fontWeight: 700,
											color: earmarks.available < 0 ? '#d32f2f' : '#1976d2',
										}}>
										{formatCurrency({ value: earmarks.available, currency: account.currency })}
									</Typography>
								</Box>
								{earmarks.plans.length > 0 && (
									<Box>
										<Typography variant="caption" color="text.secondary">
											Spend Plans
										</Typography>
										<Typography variant="body2" sx={{ fontWeight: 500 }}>
											{earmarks.plans.map((p) => p.name || `Plan #${p.id}`).join(', ')}
										</Typography>
									</Box>
								)}
							</Stack>
						</Box>
					</Grid>
				)}

				{account.isReimbursement && (
					<Grid item xs={12} md={md}>
						<CardReimbursed account={account} />
					</Grid>
				)}
				<Grid item xs={12} md={8}>
					<Entities account={account} />
				</Grid>
				<Grid item xs={12} md={4}>
					<Financing account={account} />
				</Grid>
			</Grid>
		</Container>
	);
};
